import React, {
  ChangeEvent,
  FocusEvent,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { showToast } from "../toastNotification";

interface InputTextFieldProps {
  type?: string;
  name: string;
  placeholder: string;
  value: string | File | null | undefined;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  label?: string;
  endAdornment?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  rows?: number;
  backgroundColor?: string;
  readOnly?: boolean;
  maxLength?: number;
  infoTable?: ReactNode;
  marginBottom?: string;
  showCharCount?: boolean;
}

const CustomInput: React.FC<InputTextFieldProps> = ({
  type,
  name,
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  endAdornment,
  required,
  disabled,
  error,
  rows,
  backgroundColor,
  readOnly,
  maxLength,
  infoTable,
  showCharCount,
  marginBottom = "8px",
}) => {
  const hasShownToast = useRef(false);

  useEffect(() => {
    const currentLength = value?.toString()?.length || 0;

    if (showCharCount && maxLength && currentLength === maxLength) {
      if (!hasShownToast.current) {
        showToast(` Only ${maxLength} characters allowed.`, "error");

        hasShownToast.current = true;
      }
    } else {
      hasShownToast.current = false;
    }
  }, [value, maxLength, showCharCount]);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: error ? "0px" : marginBottom,
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "medium",
            fontSize: "12px",
            display: "flex",
            color: "#FFFFFF",
          }}
        >
          {label}{" "}
          {required && (
            <span
              style={{ color: "red", marginLeft: "5px", lineHeight: "1.5" }}
            >
              *
            </span>
          )}
          {infoTable && (
            <Tooltip
              title={infoTable}
              placement="right"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#fff",
                    color: "#222",
                    boxShadow: 2,
                    p: 1,
                    maxWidth: 350,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{ marginLeft: 0.5, color: "#ffffff", padding: "0px" }}
                tabIndex={-1}
                aria-label="Info"
              >
                <InfoOutlinedIcon fontSize="small" sx={{ padding: "0px" }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      )}
      <TextField
        autoComplete="off"
        fullWidth
        variant="outlined"
        type={type}
        name={name}
        error={!!error}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        multiline={rows ? true : false}
        rows={rows}
        inputProps={{
          readOnly,
          maxLength: typeof maxLength === "number" ? maxLength : 500,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "0px",
            borderColor: error ? "#d32f2f!important" : "#808080!important",
            height: rows ? "auto" : "40px",
            fontSize: "14px",
            backgroundColor: backgroundColor || "#393939",
            color: "#CCCCCC",
          },
          "& .MuiInputBase-input": {
            color: "#CCCCCC",
            "&::placeholder": {
              color: "#CCCCCC",
              opacity: 1,
            },
            overflow: "auto",
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              display: "none",
              margin: 0,
            },
            "&[type=number]": {
              MozAppearance: "textfield",
            },
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#CCCCCC",
            opacity: 1,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#CCCCCC !important",
          },

          "& .Mui-error": {
            color: "#d32f2f",
          },
          "& fieldset": {
            borderColor: error ? "#d32f2f !important" : "#808080",
          },
          "&:hover fieldset": {
            borderColor: error ? "#d32f2f !important" : "#808080!important",
          },
        }}
        slotProps={{
          input: {
            endAdornment: endAdornment ? (
              <InputAdornment
                position="end"
                sx={{
                  margin: 0,
                  "& .MuiIconButton-root": {
                    margin: 0,
                  },
                }}
              >
                {endAdornment}
              </InputAdornment>
            ) : null,
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {error && (
          <Typography
            className="f-12"
            sx={{
              fontWeight: "medium",
              color: "#d32f2f",
              whiteSpace: "nowrap",
            }}
          >
            {error || ""}
          </Typography>
        )}
        {showCharCount && (
          <Typography
            className="f-12"
            sx={{
              fontWeight: "medium",
              color: "#CCCCCC",
              whiteSpace: "nowrap",
              ml: "auto",
            }}
          >
            {`${value?.toString()?.length || 0}/${maxLength || 500} characters`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CustomInput;
